import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Video, Upload, Trash2, Folder, FolderOpen, Home, ChevronLeft,
  Loader2, Play, Banknote, Plus, X, FolderPlus, Pencil, GripVertical, ChevronUp, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const GRADES = [
  'الصف الأول', 'الصف الثاني', 'الصف الثالث', 'الصف الرابع', 'الصف الخامس',
  'الصف السادس', 'الصف السابع', 'الصف الثامن', 'الصف التاسع', 'الصف العاشر',
  'الصف الحادي عشر', 'الصف الثاني عشر', 'غير ذلك',
];
const SECTIONS = ['أ', 'ب', 'ج', 'د', 'هـ', 'و'];

// ── category-path folder tree (same idea/behaviour as the question bank) ─────
function buildVideoTree(videos, emptyFolders = []) {
  const root = { children: {}, items: [] };
  const ensurePath = (parts) => {
    let node = root;
    for (const part of parts) {
      if (!node.children[part]) node.children[part] = { children: {}, items: [] };
      node = node.children[part];
    }
    return node;
  };
  for (const v of videos) {
    const parts = (v.category || '').split('/').map((p) => p.trim()).filter(Boolean);
    ensurePath(parts).items.push(v);
  }
  for (const path of emptyFolders) {
    const parts = path.split('/').map((p) => p.trim()).filter(Boolean);
    if (parts.length) ensurePath(parts);
  }
  return root;
}
function countTree(node) {
  return node.items.length + Object.values(node.children).reduce((s, c) => s + countTree(c), 0);
}
function nodeAtPath(root, parts) {
  let node = root;
  for (const p of parts) { if (!node?.children[p]) return null; node = node.children[p]; }
  return node;
}

const TeacherVideoManager = ({ apiUrl, token }) => {
  const [videos, setVideos] = useState([]);
  const [emptyFolders, setEmptyFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [path, setPath] = useState([]); // current folder path
  const [uploadOpen, setUploadOpen] = useState(false);

  // drag-and-drop state
  const [dragVideoId, setDragVideoId] = useState(null);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [dragOverVideoId, setDragOverVideoId] = useState(null);

  // folder rename state
  const [renamingPath, setRenamingPath] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  // folder settings (the whole video folder is bought as one unit: price + grade)
  const [folderSettingsMap, setFolderSettingsMap] = useState({});
  const [priceDialog, setPriceDialog] = useState(null); // category whose settings are open
  const [priceValue, setPriceValue] = useState('');
  const [folderGrade, setFolderGrade] = useState('');
  const [folderSection, setFolderSection] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);

  const openFolderSettings = (fullPath) => {
    const s = folderSettingsMap[fullPath] || {};
    setPriceDialog(fullPath);
    setPriceValue(String(s.price || 0));
    setFolderGrade(s.grade || '');
    setFolderSection(s.section || '');
  };

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const jsonHeaders = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }),
    [token],
  );

  const fetchVideos = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/videos`, { headers: authHeaders });
      if (r.ok) setVideos(await r.json());
    } catch { /* ignore */ }
  }, [apiUrl, authHeaders]);

  const fetchFolders = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/video-folders`, { headers: authHeaders });
      if (r.ok) {
        const data = await r.json();
        setEmptyFolders(data.map((f) => f.path));
      }
    } catch { /* ignore */ }
    try {
      const r2 = await fetch(`${apiUrl}/api/teacher/folder-settings?scope=video`, { headers: authHeaders });
      if (r2.ok) {
        const arr = await r2.json();
        const m = {};
        (arr || []).forEach((s) => { m[s.category] = s; });
        setFolderSettingsMap(m);
      }
    } catch { /* ignore */ }
  }, [apiUrl, authHeaders]);

  const saveFolderPrice = async () => {
    setSavingPrice(true);
    try {
      const r = await fetch(`${apiUrl}/api/teacher/folder-settings`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({
          category: priceDialog,
          price: Math.max(0, Number(priceValue) || 0),
          grade: folderGrade || '',
          section: folderSection || '',
          scope: 'video',
        }),
      });
      if (r.ok) { toast.success('تم حفظ إعدادات المجلد'); setPriceDialog(null); await refresh(); }
      else toast.error('فشل الحفظ');
    } catch { toast.error('فشل الاتصال بالخادم'); }
    setSavingPrice(false);
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchVideos(), fetchFolders()]);
    setLoading(false);
  }, [fetchVideos, fetchFolders]);

  useEffect(() => { refresh(); }, [refresh]);

  const tree = useMemo(() => buildVideoTree(videos, emptyFolders), [videos, emptyFolders]);
  const currentNode = nodeAtPath(tree, path) || { children: {}, items: [] };
  const folderNames = Object.keys(currentNode.children).sort((a, b) => a.localeCompare(b, 'ar'));
  const currentPathStr = path.join('/');
  // current folder's videos, ordered by their persisted order
  const currentItems = [...currentNode.items].sort((a, b) => (a.order || 0) - (b.order || 0));

  // ── folder operations (mirror of the question bank) ───────────────────────
  const createFolder = async () => {
    const existing = new Set(folderNames);
    let base = 'مجلد جديد';
    let name = base; let i = 2;
    while (existing.has(name)) name = `${base} (${i++})`;
    const newPath = currentPathStr ? `${currentPathStr}/${name}` : name;
    try {
      const r = await fetch(`${apiUrl}/api/video-folders`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify({ path: newPath }),
      });
      if (r.ok) { await fetchFolders(); setRenamingPath(newPath); setRenameValue(name); }
      else toast.error('فشل إنشاء المجلد');
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const commitRename = async (oldPath, value) => {
    const trimmed = (value || '').trim();
    setRenamingPath(null);
    if (!trimmed) return;
    const parentParts = oldPath.split('/').slice(0, -1);
    const newPath = [...parentParts, trimmed].join('/');
    if (newPath === oldPath) return;
    try {
      const r = await fetch(`${apiUrl}/api/video-folders/rename`, {
        method: 'PUT', headers: jsonHeaders,
        body: JSON.stringify({ old_path: oldPath, new_path: newPath }),
      });
      if (r.ok) { await refresh(); }
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'فشل إعادة التسمية'); }
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const deleteFolder = async (name) => {
    const fullPath = currentPathStr ? `${currentPathStr}/${name}` : name;
    const node = currentNode.children[name];
    if (node && countTree(node) > 0) { toast.error('المجلد غير فارغ — انقل محتوياته أولاً'); return; }
    if (!window.confirm(`حذف المجلد «${name}»؟`)) return;
    try {
      const r = await fetch(`${apiUrl}/api/video-folders?path=${encodeURIComponent(fullPath)}`, {
        method: 'DELETE', headers: authHeaders,
      });
      if (r.ok) await fetchFolders();
      else { const e = await r.json().catch(() => ({})); toast.error(e.detail || 'فشل حذف المجلد'); }
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const renameVideo = async (v) => {
    const next = window.prompt('اسم الفيديو الجديد:', v.title);
    if (next == null) return;
    const title = next.trim();
    if (!title || title === v.title) return;
    try {
      const r = await fetch(`${apiUrl}/api/videos/${v.id}`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ title }),
      });
      if (r.ok) { toast.success('تم تغيير الاسم'); fetchVideos(); }
      else toast.error('فشل تغيير الاسم');
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`حذف الفيديو «${v.title}»؟`)) return;
    try {
      const r = await fetch(`${apiUrl}/api/videos/${v.id}`, { method: 'DELETE', headers: authHeaders });
      if (r.ok) { toast.success('تم حذف الفيديو'); fetchVideos(); }
      else toast.error('فشل الحذف');
    } catch { toast.error('حدث خطأ'); }
  };

  // ── drag and drop ─────────────────────────────────────────────────────────
  const moveVideoToCategory = async (videoId, category) => {
    try {
      const r = await fetch(`${apiUrl}/api/videos/${videoId}`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ category }),
      });
      if (r.ok) { toast.success('تم نقل الفيديو'); fetchVideos(); }
      else toast.error('فشل النقل');
    } catch { toast.error('فشل الاتصال بالخادم'); }
  };

  const persistOrder = async (orderedIds) => {
    try {
      await fetch(`${apiUrl}/api/videos/reorder`, {
        method: 'POST', headers: jsonHeaders, body: JSON.stringify({ ordered_ids: orderedIds }),
      });
    } catch { /* best-effort */ }
  };

  const applyOrder = (ids) => {
    const orderMap = new Map(ids.map((id, idx) => [id, idx]));
    setVideos((prev) => prev.map((v) => (orderMap.has(v.id) ? { ...v, order: orderMap.get(v.id) } : v)));
    persistOrder(ids);
  };

  const onDropReorder = (targetId) => {
    if (!dragVideoId || dragVideoId === targetId) { setDragOverVideoId(null); return; }
    const ids = currentItems.map((v) => v.id);
    const from = ids.indexOf(dragVideoId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) { setDragOverVideoId(null); return; }
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    applyOrder(ids);
    setDragOverVideoId(null);
  };

  // Reliable arrow-based reordering (works without drag)
  const moveVideo = (v, dir) => {
    const ids = currentItems.map((x) => x.id);
    const i = ids.indexOf(v.id);
    const j = i + dir;
    if (i === -1 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    applyOrder(ids);
  };

  const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} م.ب` : `${Math.max(1, Math.round(b / 1024))} ك.ب`);

  return (
    <div className="space-y-3 bg-gradient-to-br from-[#FAF6EC] to-[#F1E8D2] rounded-2xl p-3 md:p-4 border border-[#E8DFCF]" dir="rtl">
      {/* Title + actions */}
      <div className="flex items-center justify-between flex-wrap gap-2 px-1">
        <div className="flex items-center gap-2 text-[#412402]">
          <Video className="w-5 h-5 text-amber-700" />
          <span className="font-bold">الفيديوهات المسجّلة</span>
          <span className="text-xs text-[#412402]/50">— {videos.length} فيديو</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={createFolder} data-testid="create-video-folder-btn">
            <FolderPlus className="w-4 h-4 ml-1" /> مجلد جديد
          </Button>
          <Button onClick={() => setUploadOpen(true)} className="bg-amber-700 hover:bg-amber-800" data-testid="upload-video-btn">
            <Upload className="w-4 h-4 ml-1" /> رفع فيديو
          </Button>
        </div>
      </div>

      {/* Breadcrumb (drop targets for moving up) */}
      <div className="flex items-center gap-1 bg-white/70 border border-[#E5E5E5] rounded-xl px-3 py-2 text-sm flex-wrap">
        <button
          onClick={() => setPath([])}
          onDragOver={(e) => { if (dragVideoId) { e.preventDefault(); } }}
          onDrop={() => { if (dragVideoId) moveVideoToCategory(dragVideoId, ''); }}
          className="flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-amber-50 text-[#412402] font-medium"
        >
          <Home className="w-3.5 h-3.5 text-amber-700" /> الفيديوهات
        </button>
        {path.map((part, idx) => {
          const segPath = path.slice(0, idx + 1).join('/');
          return (
            <React.Fragment key={idx}>
              <ChevronLeft className="w-3.5 h-3.5 text-[#412402]/40" />
              <button
                onClick={() => setPath(path.slice(0, idx + 1))}
                onDragOver={(e) => { if (dragVideoId) e.preventDefault(); }}
                onDrop={() => { if (dragVideoId) moveVideoToCategory(dragVideoId, segPath); }}
                className="px-1.5 py-0.5 rounded hover:bg-amber-50 text-[#412402] truncate max-w-[160px]"
              >
                {part}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      {/* Explorer body */}
      <div className="bg-white/80 border border-[#E5E5E5] rounded-xl min-h-[300px] p-3">
        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin text-amber-600 mx-auto" /></div>
        ) : folderNames.length === 0 && currentItems.length === 0 ? (
          <div className="py-16 text-center text-[#412402]/50 text-sm">
            <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
            هذا المجلد فارغ. أنشئ مجلداً أو ارفع فيديو.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Folders */}
            {folderNames.length > 0 && (
              <div>
                <div className="px-1 mb-1 text-[11px] font-semibold text-[#412402]/60">مجلدات</div>
                <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-2">
                  {folderNames.map((name) => {
                    const fullPath = currentPathStr ? `${currentPathStr}/${name}` : name;
                    const isRenaming = renamingPath === fullPath;
                    return (
                      <div
                        key={name}
                        onDragOver={(e) => { if (dragVideoId) { e.preventDefault(); setDragOverFolder(name); } }}
                        onDragLeave={() => setDragOverFolder((f) => (f === name ? null : f))}
                        onDrop={() => { if (dragVideoId) moveVideoToCategory(dragVideoId, fullPath); setDragOverFolder(null); }}
                        className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl border transition-colors ${
                          dragOverFolder === name ? 'bg-amber-100 border-amber-400' : 'border-transparent hover:bg-amber-50 hover:border-amber-200'
                        }`}
                      >
                        {isRenaming ? (
                          /* While renaming: NO <button> wrapper — a button would
                             treat Space as "activate" and navigate into the folder
                             instead of typing a space into the name. */
                          <div className="flex flex-col items-center gap-1 w-full">
                            <FolderOpen className="w-12 h-12 text-amber-500" />
                            <input
                              autoFocus
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onBlur={() => commitRename(fullPath, renameValue)}
                              onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') commitRename(fullPath, renameValue); if (e.key === 'Escape') setRenamingPath(null); }}
                              className="text-sm text-center border border-amber-300 rounded px-1 w-full"
                            />
                            <span className="text-[11px] text-[#412402]/50">{countTree(currentNode.children[name])} عنصر</span>
                          </div>
                        ) : (
                          <button
                            onDoubleClick={() => setPath([...path, name])}
                            onClick={() => setPath([...path, name])}
                            className="flex flex-col items-center gap-1 w-full"
                          >
                            <FolderOpen className="w-12 h-12 text-amber-500" />
                            <span className="text-sm font-medium text-[#412402] line-clamp-2 text-center">{name}</span>
                            <span className="text-[11px] text-[#412402]/50">{countTree(currentNode.children[name])} عنصر</span>
                          </button>
                        )}
                        {/* price badge (top-right) */}
                        <div className="absolute top-1 right-1">
                          {(folderSettingsMap[fullPath]?.price || 0) > 0 ? (
                            <span className="text-[9px] font-bold bg-amber-600 text-white px-1.5 py-0.5 rounded-full">{folderSettingsMap[fullPath].price} د.أ</span>
                          ) : (
                            <span className="text-[9px] font-bold bg-green-600 text-white px-1.5 py-0.5 rounded-full">مجاني</span>
                          )}
                        </div>
                        {/* folder actions */}
                        <div className="absolute top-1 left-1 flex gap-0.5 opacity-0 group-hover:opacity-100">
                          <button title="إعدادات المجلد (السعر والصف)" onClick={() => openFolderSettings(fullPath)}
                            className="p-1 rounded bg-white/80 hover:bg-amber-100 text-[#412402]/70">
                            <Banknote className="w-3 h-3" />
                          </button>
                          <button title="إعادة تسمية" onClick={() => { setRenamingPath(fullPath); setRenameValue(name); }}
                            className="p-1 rounded bg-white/80 hover:bg-amber-100 text-[#412402]/70">
                            <Pencil className="w-3 h-3" />
                          </button>
                          <button title="حذف المجلد" onClick={() => deleteFolder(name)}
                            className="p-1 rounded bg-white/80 hover:bg-red-100 text-red-600">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Videos at this level (draggable + reorderable) */}
            {currentItems.length > 0 && (
              <div>
                <div className="px-1 mb-1 text-[11px] font-semibold text-[#412402]/60">
                  الفيديوهات <span className="font-normal">— اسحب لإعادة الترتيب أو أفلت على مجلد للنقل</span>
                </div>
                <div className="space-y-2">
                  {currentItems.map((v) => (
                    <div
                      key={v.id}
                      draggable
                      onDragStart={() => setDragVideoId(v.id)}
                      onDragEnd={() => { setDragVideoId(null); setDragOverVideoId(null); setDragOverFolder(null); }}
                      onDragOver={(e) => { if (dragVideoId && dragVideoId !== v.id) { e.preventDefault(); setDragOverVideoId(v.id); } }}
                      onDragLeave={() => setDragOverVideoId((id) => (id === v.id ? null : id))}
                      onDrop={() => onDropReorder(v.id)}
                      className={`flex items-center gap-3 p-2 rounded-lg border bg-white transition-colors ${
                        dragOverVideoId === v.id ? 'border-amber-400 bg-amber-50' : 'border-[#E8DFCF]'
                      } ${dragVideoId === v.id ? 'opacity-50' : ''}`}
                      data-testid={`teacher-video-${v.id}`}
                    >
                      <div className="flex flex-col items-center shrink-0">
                        <button onClick={() => moveVideo(v, -1)} title="تحريك لأعلى" className="text-[#412402]/40 hover:text-amber-700 leading-none">
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <GripVertical className="w-4 h-4 text-[#412402]/30 cursor-grab" />
                        <button onClick={() => moveVideo(v, 1)} title="تحريك لأسفل" className="text-[#412402]/40 hover:text-amber-700 leading-none">
                          <ChevronDown className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center shrink-0">
                        <Play className="w-5 h-5 text-amber-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#412402] text-sm truncate">{v.title}</p>
                        <div className="flex flex-wrap items-center gap-2 text-[11px] text-[#412402]/50 mt-0.5">
                          <span>{v.grade}{v.section ? ` / ${v.section}` : ''}</span>
                          <span>•</span>
                          <span className="uppercase">{(v.ext || '').replace('.', '')}</span>
                          <span>•</span>
                          <span>{fmtSize(v.size_bytes || 0)}</span>
                          {!v.is_active && <Badge className="bg-gray-200 text-gray-600 text-[9px]">معطّل</Badge>}
                        </div>
                      </div>
                      <a
                        href={`${apiUrl}/api/videos/${v.id}/stream?token=${encodeURIComponent(token)}`}
                        target="_blank" rel="noreferrer"
                        className="shrink-0 text-amber-700 hover:bg-amber-50 rounded-md p-2"
                        title="معاينة"
                      >
                        <Play className="w-4 h-4" />
                      </a>
                      <Button size="sm" variant="ghost" onClick={() => renameVideo(v)} className="text-blue-600 shrink-0" title="إعادة تسمية">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(v)} className="text-red-600 shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

    

      <VideoUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        apiUrl={apiUrl}
        token={token}
        defaultCategory={currentPathStr}
        onUploaded={() => fetchVideos()}
      />

      {/* Folder settings dialog (the whole video folder is bought as one unit) */}
      <Dialog open={!!priceDialog} onOpenChange={(v) => { if (!v) setPriceDialog(null); }}>
        <DialogContent dir="rtl" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-amber-700" /> إعدادات المجلد
            </DialogTitle>
            <DialogDescription>{priceDialog || '—'} — يشتري الطالب المجلد كاملاً من رصيده.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>الصف (لكل المجلد)</Label>
                <Select value={folderGrade || '__none__'} onValueChange={(v) => setFolderGrade(v === '__none__' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="بدون تحديد" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">بدون تحديد</SelectItem>
                    {GRADES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>الشعبة (اختياري)</Label>
                <Select value={folderSection || '__all__'} onValueChange={(v) => setFolderSection(v === '__all__' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder="جميع الشعب" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">جميع الشعب</SelectItem>
                    {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-[#412402]/50 -mt-1">يُطبَّق الصف/الشعبة على كل فيديوهات المجلد تلقائياً.</p>
            <div>
              <Label>السعر (د.أ)</Label>
              <Input type="number" min={0} step="1" value={priceValue}
                onChange={(e) => setPriceValue(e.target.value)} placeholder="0 = مجاني" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceDialog(null)}>إلغاء</Button>
            <Button onClick={saveFolderPrice} disabled={savingPrice} className="bg-amber-700 hover:bg-amber-800">
              {savingPrice ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null} حفظ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Upload dialog ─────────────────────────────────────────────────────────────
const VideoUploadDialog = ({ open, onOpenChange, apiUrl, token, defaultCategory, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: '', description: '', grade: '', section: '', category: defaultCategory || '',
    duration_label: '',
  });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setFile(null);
      setProgress(0);
      setForm((f) => ({ ...f, category: defaultCategory || '' }));
    }
  }, [open, defaultCategory]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ok = /\.(mp4|mov|mkv)$/i.test(f.name);
    if (!ok) { toast.error('الصيغ المدعومة: MP4 أو MOV أو MKV فقط'); return; }
    setFile(f);
    if (!form.title) setForm((p) => ({ ...p, title: f.name.replace(/\.[^.]+$/, '') }));
  };

  const submit = () => {
    if (!file) return toast.error('اختر ملف فيديو');
    if (!form.title.trim()) return toast.error('أدخل عنوان الفيديو');

    const fd = new FormData();
    fd.append('file', file);
    fd.append('title', form.title);
    fd.append('description', form.description);
    fd.append('grade', form.grade);
    fd.append('section', form.section);
    fd.append('category', form.category);
    fd.append('duration_label', form.duration_label);
    fd.append('is_active', 'true');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiUrl}/api/videos`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      setUploading(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        const created = JSON.parse(xhr.responseText);
        toast.success('تم رفع الفيديو');
        onUploaded?.(created);
        onOpenChange(false);
      } else {
        let msg = 'فشل رفع الفيديو';
        try { msg = JSON.parse(xhr.responseText).detail || msg; } catch { /* ignore */ }
        toast.error(msg);
      }
    };
    xhr.onerror = () => { setUploading(false); toast.error('فشل الاتصال بالخادم'); };
    setUploading(true);
    setProgress(0);
    xhr.send(fd);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!uploading) onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-amber-700" /> رفع فيديو مسجّل
          </DialogTitle>
          <DialogDescription>الصيغ المدعومة: MP4 / MOV / MKV</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>ملف الفيديو</Label>
            {file ? (
              <div className="flex items-center gap-2 p-2 border border-amber-200 bg-amber-50 rounded-lg">
                <Video className="w-5 h-5 text-amber-700 shrink-0" />
                <span className="flex-1 min-w-0 truncate text-sm">{file.name}</span>
                <Button size="sm" variant="ghost" onClick={() => setFile(null)} disabled={uploading}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-amber-300 rounded-lg cursor-pointer hover:bg-amber-50">
                <Plus className="w-8 h-8 text-amber-500" />
                <span className="text-sm text-[#412402]/60">اختر ملف MP4 / MOV / MKV</span>
                <input type="file" accept=".mp4,.mov,.mkv,video/*" className="hidden" onChange={pickFile} data-testid="video-file-input" />
              </label>
            )}
          </div>

          <div>
            <Label>العنوان</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="مثال: شرح المبتدأ والخبر" />
          </div>
          <div>
            <Label>الوصف</Label>
            <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <div>
            <Label className="flex items-center gap-1"><Folder className="w-4 h-4 text-amber-700" /> المجلد / التصنيف</Label>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="مثال: النحو/الدرس الأول (اختياري)" />
            <p className="text-[11px] text-[#412402]/50 mt-1">استخدم "/" لمجلدات متداخلة. الصف/الشعبة والسعر تُحدَّد على مستوى المجلد (زر 💵).</p>
          </div>

          <div>
            <Label>مدة الفيديو (نص)</Label>
            <Input value={form.duration_label} onChange={(e) => setForm({ ...form, duration_label: e.target.value })} placeholder="مثال: 12:30" />
            <p className="text-[11px] text-[#412402]/50 mt-1">السعر يُحدَّد على مستوى المجلد (زر 💵 على المجلد). يشتري الطالب المجلد كاملاً.</p>
          </div>

          {uploading && (
            <div className="space-y-1">
              <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-600 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-center text-[#412402]/60">جارٍ الرفع... {progress}%</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>إلغاء</Button>
          <Button onClick={submit} disabled={uploading || !file} className="bg-amber-700 hover:bg-amber-800" data-testid="confirm-upload-video-btn">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Upload className="w-4 h-4 ml-2" />}
            رفع
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeacherVideoManager;
